import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
} from "@mui/material";
import { InspModelItem, RuleItem } from "@types";
import { useState, useEffect } from "react";
import { parseStringToArray } from "../../utils/utils";
import { editInspRule } from "../../services/inspRuleService";
import { toast } from "react-toastify";
import { Pencil } from "lucide-react";
import { Stack } from "@mui/system";
import { CloseIcon } from "common-lib";
import { t } from "i18next";

interface Props {
  rule: RuleItem | null;
  modelList: InspModelItem[];
  open: boolean;
  onClose: () => void;
  onUpdated?: (updatedRule: RuleItem) => void;
}

export default function RuleDetailModal({ rule, modelList, open, onClose, onUpdated }: Props) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    image_regex: "",
    recipe_regex: "",
    model_uuid: "",
  });
  type FormKey = keyof typeof form;
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (rule && modelList.length > 0) {
      console.log(rule);
      const exists = modelList.some((m) => m.uuid === rule.inspModel.uuid);

      setForm({
        name: rule.name ?? "",
        description: rule.description ?? "",
        image_regex: rule.image_regex ?? "",
        recipe_regex: rule.recipe_regex ?? "",
        model_uuid: exists ? rule.inspModel.uuid : "",
      });
    }
  }, [rule, modelList]);

  if (!rule) return null;

  const handleSave = async () => {
    if (window.confirm(t("SaveInspRuleConfirmMsg"))) {
      try {
        console.log(form.image_regex, form.recipe_regex);
        await editInspRule(rule.id, {
          name: form.name,
          description: form.description,
          model_uuid: form.model_uuid,
          image_regex: form.image_regex,
          recipe_regex: form.recipe_regex,
        });
        onUpdated?.({
          ...rule,
          name: form.name,
          description: form.description,
          image_regex: form.image_regex,
          recipe_regex: form.recipe_regex,
          inspModel: modelList.find((m) => m.uuid === form.model_uuid)
            ? {
                ...rule.inspModel,
                uuid: form.model_uuid,
                name: modelList.find((m) => m.uuid === form.model_uuid)!.name,
              }
            : rule.inspModel,
        });
        onClose();
      } catch (error: any) {
        toast.error(t("EditInspRuleFailMsg"));
      }
    }
  };

  const handleClose = () => {
    setIsEditing(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          width: 480,
          bgcolor: "background.paper",
          borderRadius: 3,
          mx: "auto",
          mt: "10%",
          outline: "none",
          overflow: "hidden",
          border: "0.5px solid",
          borderColor: "grey.200",
          boxShadow: "0 16px 48px rgba(0,0,0,0.12)",
        }}
      >
        {/* 헤더 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.75,
            borderBottom: "0.5px solid",
            borderColor: "grey.200",
            bgcolor: "grey.50",
          }}
        >
          <Typography fontSize={14} fontWeight={500}>
            {t("InspRuleInfo")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {!isEditing && (
              <IconButton
                size="small"
                onClick={() => setIsEditing(true)}
                sx={{
                  width: 28,
                  height: 28,
                  border: "0.5px solid",
                  borderColor: "grey.200",
                  borderRadius: 1,
                  color: "text.secondary",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <Pencil size={13} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                width: 28,
                height: 28,
                border: "0.5px solid",
                borderColor: "grey.200",
                borderRadius: 1,
                color: "text.secondary",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>

        {/* 바디 */}
        <Box sx={{ px: 2.5, py: 2 }}>
          <Stack spacing={1.75}>
            {[
              { label: t("InspRuleName"), key: "name" as FormKey },
              { label: t("Description"), key: "description" as FormKey },
              { label: t("ImageRegex"), key: "image_regex" as FormKey },
              { label: t("RecipeRegex"), key: "recipe_regex" as FormKey },
            ].map(({ label, key }) => (
              <Box key={key}>
                {isEditing ? (
                  <TextField
                    label={label}
                    fullWidth
                    value={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    sx={{ "& .MuiOutlinedInput-root": { fontSize: 13, borderRadius: 1.5 } }}
                  />
                ) : (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      bgcolor: "grey.50",
                      borderRadius: 1.5,
                      border: "0.5px solid",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography fontSize={11} color="text.secondary" mb={0.4}>
                      {label}
                    </Typography>
                    <Typography fontSize={13} fontWeight={500}>
                      {form[key] || "-"}
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}

            {/* 모델 필드 */}
            <Box>
              {isEditing ? (
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: 13 }}>{t("Model")}</InputLabel>
                  <Select
                    value={form.model_uuid}
                    label={t("Model")}
                    onChange={(e) => setForm({ ...form, model_uuid: e.target.value })}
                    sx={{ fontSize: 13, borderRadius: 1.5 }}
                  >
                    {modelList.map((model) => (
                      <MenuItem key={model.uuid} value={model.uuid} sx={{ fontSize: 13 }}>
                        {model.name}
                        <Box component="span" sx={{ fontSize: 11, color: "text.secondary", ml: 0.75 }}>
                          ({model.uuid.slice(0, 6)})
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    bgcolor: "grey.50",
                    borderRadius: 1.5,
                    border: "0.5px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Typography fontSize={11} color="text.secondary" mb={0.4}>
                    {t("Model")}
                  </Typography>
                  <Typography fontSize={13} fontWeight={500}>
                    {modelList.find((m) => m.uuid === form.model_uuid)?.name || "-"}
                  </Typography>
                </Box>
              )}
            </Box>
          </Stack>
        </Box>

        {/* 푸터 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 2.5,
            py: 1.75,
            borderTop: "0.5px solid",
            borderColor: "grey.200",
            bgcolor: "grey.50",
          }}
        >
          {isEditing ? (
            <>
              <Button
                size="small"
                onClick={() => setIsEditing(false)}
                sx={{
                  fontSize: 12,
                  px: 1.75,
                  textTransform: "none",
                  borderColor: "grey.300",
                  color: "text.secondary",
                }}
                variant="outlined"
              >
                {t("Cancel")}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  handleSave();
                  setIsEditing(false);
                }}
                sx={{ fontSize: 12, px: 1.75, textTransform: "none", borderRadius: 1.5 }}
              >
                {t("Save")}
              </Button>
            </>
          ) : (
            <Button
              size="small"
              onClick={handleClose}
              sx={{
                fontSize: 12,
                px: 1.75,
                textTransform: "none",
                borderColor: "grey.300",
                color: "text.secondary",
              }}
              variant="outlined"
            >
              {t("Close")}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
